import React, { useState, useEffect, useRef } from "react";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Checkbox, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";


import { Button } from "../../../components/Button/Button";
import axiosInstant from "../../../api";
import toast from "react-hot-toast";
import { IconUser, IconPlus } from "../../../utils/icons";
import { ExpertKeyPointsModal } from "../../../components/ExpertKeyPointsModal/ExpertKeyPointsModal";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import {
  changeSelectionRoster,
  rosterErrorMessage,
} from "../../../utils/selectionRoster";
import { ExpertKeyPointsList } from "../../../components/ExpertKeyPoints/ExpertKeyPoints";
import { ScoreRatePills } from "./ScoreRatePills";
import { AlgoScorePerProduct } from "./AlgoScorePerProduct";
import { ExpertOptStatusBar } from "./ExpertOptStatusBar";
import { useUser } from "../../../hooks/useUser";
import { isInternal } from "../../../utils";
import { formatProfessionsWithExperience } from "../../../utils/professionExperience";
import { describeSharedSiblings, SHOT_SELECTION_STATUSES, getBpp } from "../helper";
import { ExpertIdPill } from "../../../components/ExpertIdPill/ExpertIdPill";
import ExpertContactPopover from "./ExpertContactPopover";
import { cldUrl } from "../../../utils/cloudinary";

const useDebouncedFieldSaver = (project, pro, fieldName, delay = 400, onPendingChange) => {
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  // "Pending" spans from the keystroke that schedules a save until the PATCH
  // settles — the whole window where the server may not have the value yet.
  // Callers use it to hold off actions the server validates against this
  // field (e.g. the opt-in send's rate gate).
  const pendingRef = useRef(false);
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;
  const setPending = (value) => {
    if (pendingRef.current === value) return;
    pendingRef.current = value;
    onPendingChangeRef.current?.(value);
  };

  const scheduleSave = (value) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPending(true);
    timerRef.current = setTimeout(async () => {
      const effectiveBrandId = project?.brand_id || project?.brands?.find((b) => b.rmn !== true)?.id;
      if (!effectiveBrandId || !project?.id || !pro?.userid) {
        setPending(false);
        return;
      }
      try {
        setSaving(true);
        await axiosInstant.patch(
          `/brands/${effectiveBrandId}/projects/${project.id}/pros/${pro.userid}/${fieldName}`,
          { [fieldName]: value }
        );
      } catch (error) {
        console.error(`Error updating expert ${fieldName}:`, error);
        toast.error(
          error?.response?.status === 423
            ? "This selection is locked — roster edits are frozen."
            : `Couldn't save the expert's ${fieldName}. Try again.`
        );
      } finally {
        setSaving(false);
        setPending(false);
      }
    }, delay);
  };

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // An unmounted card can't finish its save — don't leave the parent
    // waiting on a pending flag forever.
    setPending(false);
  }, []);

  return { saving, scheduleSave };
};

/** @type {{ id: number, title?: string, status?: string }[]} */
const NO_SHARED_SIBLINGS = [];

const ExpertSelectionCard = ({
  pro,
  isSelected,
  recommendationCheck,
  onUpdate,
  project,
  onScoreChange = (_userId, _score) => { },
  onRateChange = (_userId, _rate) => { },
  onRateSavingChange = (_userId, _saving) => { },
  selectedExperts = [],
  approvalCheck,
  isTopRecommendation = false,
  setSelectedExperts = undefined,
  isApprovalPage = false,
  currentOrder = 0,
  onOrderChange = (_id, _order) => { },
  isRankingMode = false,
  hideKeyPoints = false,
  customHeight = null,
  disableSelection = false,
  canEditRanking = true,
  hideSelection = false,
  forceHideOptStatus = false,
  forceHideRate = false,
  publicToken = undefined,
  // Algorithm-generated scores from /brand-projects/:id/expert-scores.
  // Optional — when omitted (dashboard cards, public-token pages) the chips
  // don't render. Defaults to {} (not undefined) so TS treats it as optional
  // for .tsx callers instead of pinning the inferred type.
  algoProducts = /** @type {any[]} */ ([]),
  algoScores = {},
  algoBreakdown = {},
  algoIndustryScores = {},
  algoExpertTypes = {},
  algoProfessions = /** @type {any[]} */ ([]),
  // Other projects that share this project's selection process. When non-empty,
  // add/remove here also affects them (the backend keys pros by process), so we
  // confirm removals. Empty on historical 1-project processes.
  sharedSiblingProjects = NO_SHARED_SIBLINGS,
  onRequestReopen = /** @type {any} */ (undefined),
}) => {
  const { role } = useUser();

  const initialScore = pro?.projects?.[0]?.BrandProjectPro?.score;
  const [scoreInput, setScoreInput] = useState(
    initialScore !== undefined && initialScore !== null
      ? parseFloat(initialScore)
      : undefined
  );
  // Provenance: did an AM manually override the algorithm-derived score?
  const [scoreOverridden, setScoreOverridden] = useState(
    !!pro?.projects?.[0]?.BrandProjectPro?.score_overridden
  );
  const { saving: savingScore, scheduleSave: scheduleSaveScore } =
    useDebouncedFieldSaver(project, pro, "score");

  // The pro's best-fit Expert IQ score across the project's products, scaled
  // to 0-100. Used to show a default score on unselected pros and to populate
  // the input optimistically on "reset to algo".
  const algoMax = (() => {
    const vals = Object.values(algoScores || {})
      .map(Number)
      .filter((n) => Number.isFinite(n));
    return vals.length ? Math.round(Math.max(...vals) * 100) : undefined;
  })();

  // Revert a manual override: clear the flag, show the algo value immediately,
  // and PATCH a null score so the backend recomputes and persists the default.
  const handleResetScore = () => {
    setScoreOverridden(false);
    setScoreInput(algoMax);
    if (!isRankingMode && !recommendationCheck && algoMax !== undefined) {
      onScoreChange?.(pro.userid, algoMax);
    }
    scheduleSaveScore(null);
  };

  const initialRate = pro?.projects?.[0]?.BrandProjectPro?.rate;
  const [rateInput, setRateInput] = useState(
    initialRate !== undefined && initialRate !== null && initialRate !== ""
      ? String(parseFloat(initialRate))
      : ""
  );
  const { saving: savingRate, scheduleSave: scheduleSaveRate } =
    useDebouncedFieldSaver(project, pro, "rate", 400, (pending) =>
      onRateSavingChange?.(pro.userid, pending)
    );

  const infoPanelRef = useRef(null);
  const navigate = useNavigate();
  const [isAddingToProject, setIsAddingToProject] = useState(false);
  const [showKeyPointsModal, setShowKeyPointsModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Locked selection (at/past Pre-Production): the roster is frozen, so the
  // card renders read-only regardless of which page mounted it.
  const processLocked = !!project?.process_locked;
  const selectionShot = SHOT_SELECTION_STATUSES.includes(project?.status);
  const canReopenSelection =
    processLocked && !selectionShot && !!onRequestReopen;
  // eslint-disable-next-line no-param-reassign
  disableSelection = disableSelection || processLocked;

  const projectBio = pro.projects?.[0]?.BrandProjectPro?.project_bio?.trim() || '';
  const isChecked = selectedExperts.includes(pro.userid);
  const showContact =
    isInternal(role) &&
    !publicToken &&
    (isSelected || recommendationCheck || (isRankingMode && currentOrder > 0));
  // Sit the Contact button beside the ALGO row when it exists, otherwise pair it
  // with the SCORE row so it never lands on a line of its own.
  const contactInScoreRow = showContact && algoProducts.length === 0;
  const checkboxNumber = isChecked
    ? selectedExperts.indexOf(pro.userid) + 1
    : null;

  const handleCheckboxToggle = () => {
    if (isRankingMode && onOrderChange) {
      if (currentOrder > 0) {
        onOrderChange(pro.userid, 0);
      } else {
        onOrderChange(pro.userid, -1);
      }
      return;
    }

    if (!setSelectedExperts) return;

    if (isChecked) {
      setSelectedExperts((prev) => prev.filter((id) => id !== pro.userid));
    } else {
      setSelectedExperts((prev) => [...prev, pro.userid]);
    }
  };

  const performCheckboxChange = async (isNowChecked) => {
    setIsAddingToProject(true);

    try {
      const effectiveBrandId = project?.brand_id || project?.brands?.find((b) => b.rmn !== true)?.id;
      await changeSelectionRoster(
        isNowChecked ? "add" : "remove",
        effectiveBrandId,
        project?.id,
        pro.userid
      );
      onUpdate();
    } catch (error) {
      console.error("Error updating expert selection:", error);
      toast.error(rosterErrorMessage(error));
    } finally {
      setIsAddingToProject(false);
    }
  };

  const handleCheckboxChange = async (e) => {
    const isNowChecked = e.target.checked;

    if (canReopenSelection) {
      const bpp = getBpp(pro);
      const isConfirmedExpert =
        bpp?.internal_confirmed === true || bpp?.user_confirmed === true;
      // Removing an UNCONFIRMED expert from a committed selection is quiet
      // bench hygiene — the API drops the row and just logs it, no rewind —
      // so only adds and confirmed-expert removals need the reopen
      // confirmation notice.
      if (isNowChecked || isConfirmedExpert) {
        onRequestReopen({
          userid: pro.userid,
          displayName: pro?.displayName || "this expert",
          action: isNowChecked ? "add" : "remove",
        });
        return;
      }
    }

    // Removing an expert removes them from the whole selection process — i.e.
    // every project that shares it. Confirm when that's more than this project.
    if (!isNowChecked && sharedSiblingProjects.length > 0) {
      setShowRemoveConfirm(true);
      return;
    }

    await performCheckboxChange(isNowChecked);
  };

  const handleScoreInputChange = (e) => {
    const value = Number(e.target.value);
    if (value >= 0 && value <= 100) {
      setScoreInput(value);
      // A manual entry pins the score so algo refreshes won't clobber it.
      setScoreOverridden(true);
      if (!isRankingMode && !recommendationCheck) {
        onScoreChange?.(pro.userid, value);
      }
      scheduleSaveScore(value);
    }
  };

  const handleRateInputChange = (e) => {
    let raw = e.target.value.replace(/[^\d.]/g, "");
    const firstDot = raw.indexOf(".");
    if (firstDot !== -1) {
      raw =
        raw.slice(0, firstDot + 1) +
        raw.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
    }
    setRateInput(raw);
    // Keep the parent's projectPros copy of the rate in step (mirrors the
    // score input above) so rate-gated UI reacts without a refetch.
    onRateChange?.(pro.userid, raw === "" ? null : raw);
    scheduleSaveRate(raw === "" ? "" : raw);
  };

  // Determine the height class
  const getHeightClass = () => {
    if (customHeight) {
      return `h-[${customHeight}px]`;
    }
    return "min-h-[480px]";
  };

  return (
    <div
      className={`flex flex-col justify-between w-full h-full min-w-[300px] rounded-[16px] border transition-all duration-200 bg-surface shadow-[shadow:var(--sh-1)] overflow-hidden ${isRankingMode && currentOrder > 0
          ? "border-[#E4C9A1] border-[3px]"
          : isTopRecommendation
          ? "border-[#E4C9A1] border-[3px]"
          : "border-border-token"
      } ${getHeightClass()}`}
      style={customHeight ? { height: `${customHeight}px` } : {}}
    >
      <ExpertOptStatusBar expert={pro} project={project} forceHide={forceHideOptStatus} onSigned={onUpdate} />
      <div className="flex flex-col gap-[10px] min-h-0 p-4 flex-1" ref={infoPanelRef}>
        <div className="flex items-start justify-between w-full gap-2">
          {pro?.avatar ? (
            <img
              className="w-[80px] h-[80px] object-cover object-top rounded-[6px] shrink-0"
              src={cldUrl(pro.avatar, { w: 160, h: 160, crop: "fill", gravity: "face" })}
              alt={pro?.displayName || "Expert"}
            />
          ) : (
            <div className="w-[80px] h-[80px] flex shrink-0 items-center justify-center rounded-[6px] bg-tint">
              <IconUser width={32} height={32} />
            </div>
          )}

          <div className="flex gap-1 items-center">
            {isTopRecommendation && (
              <div className="flex items-center gap-2 px-[10px] py-[4px] bg-tint rounded-[32px] h-[24px] whitespace-nowrap">
                <span className="text-[12px] font-[450] text-accent-text font-circular whitespace-nowrap">
                  Top Recommendation!
                </span>
              </div>
            )}

            {!hideSelection && (!approvalCheck || !isApprovalPage) && (recommendationCheck || isSelected || (!recommendationCheck && !isRankingMode)) &&
              (recommendationCheck || isRankingMode ? (
                <div className="shrink-0">
                  {isRankingMode && currentOrder > 0 && !canEditRanking ? (
                    <div className="w-[24px] h-[24px] rounded-full bg-accent text-white text-[12px] flex items-center justify-center font-bold cursor-not-allowed">
                      {currentOrder}
                    </div>
                  ) : (
                    <Checkbox
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={
                        <div className={`w-[24px] h-[24px] rounded-full bg-accent text-white text-[12px] flex items-center justify-center font-bold ${disableSelection ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          {isRankingMode ? currentOrder : checkboxNumber}
                        </div>
                      }
                      checked={isRankingMode ? currentOrder > 0 : isChecked}
                      onChange={disableSelection ? undefined : handleCheckboxToggle}
                      disabled={disableSelection}
                      sx={{
                        backgroundColor: "var(--surface) !important",
                        padding: "4px",
                        color: "#DFDEDE",
                        "&.Mui-checked": {
                          color: "var(--accent)",
                        },
                        "&.Mui-disabled": {
                          color: "var(--accent)",
                          opacity: 1,
                        },
                      }}
                    />
                  )}
                </div>
              ) : (
                <div className="shrink-0 relative">
                  {isAddingToProject && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[rgba(255,255,255,0.8)] dark:bg-[rgba(24,33,47,0.8)] rounded z-10">
                      <CircularProgress size={16} sx={{ color: "var(--accent)" }} />
                    </div>
                  )}
                  <Checkbox
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    checked={isSelected}
                    onChange={handleCheckboxChange}
                    disabled={
                      isAddingToProject ||
                      (processLocked && !canReopenSelection)
                    }
                    sx={{
                      backgroundColor: "var(--surface) !important",
                      padding: "4px",
                      color: "#DFDEDE",
                      "&.Mui-checked": {
                        color: "var(--accent)",
                      },
                    }}
                  />
                </div>
              ))}
          </div>
        </div>

        {(() => {
          // Per-profession experience (Expert IQ); fall back to expertise tags
          // when the expert has no professions recorded.
          const professionText = formatProfessionsWithExperience(pro?.professions);
          const subInfo =
            professionText ||
            (pro?.expertise?.length > 0
              ? pro.expertise.map((e) => e.expertise).join(", ")
              : "");
          return subInfo ? (
            <div
              className="text-[14px] font-[450] text-text-2 font-circular overflow-hidden whitespace-nowrap text-ellipsis"
              style={{ maxWidth: "100%" }}
            >
              <span>{subInfo}</span>
            </div>
          ) : null;
        })()}

        <hr className="w-full border-t border-border-token" />

        <div className="flex items-center justify-between w-full shrink-0">
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[18px] font-semibold leading-[132%] text-text-1 font-circular max-w-[180px]">
            {pro?.displayName || "Unknown Expert"}
          </span>
          <div className="flex gap-1 items-center">
            <ExpertIdPill userid={pro?.userid} />
            {approvalCheck && isApprovalPage && (
              <div
                className="rounded-[100px] bg-tint p-[6px] cursor-pointer hover:bg-[#E8DCC8] dark:hover:bg-track transition-colors"
                onClick={() => setShowKeyPointsModal(true)}
                title="Add project-specific key points"
              >
                <IconPlus width={16} height={16} stroke="#AB854A" strokeWidth={4.5} />
              </div>
            )}
          </div>
        </div>

        {(projectBio || pro.description?.trim()) && (
          <div
            className="text-[12px] font-[450] leading-[136%] text-text-2 font-circular shrink-0"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxHeight: "3.2em",
            }}
          >
            {projectBio || pro.description}
          </div>
        )}

        {isSelected && !recommendationCheck && !isRankingMode && (
          <div className="flex items-center justify-between gap-2 min-w-0 my-2">
            <div className="min-w-0 flex-1">
              <ScoreRatePills
                editable={!processLocked}
                showRequired
                scoreValue={scoreInput}
                onScoreChange={handleScoreInputChange}
                rateValue={rateInput}
                onRateChange={handleRateInputChange}
                savingScore={savingScore}
                savingRate={savingRate}
                hideRate={forceHideRate}
                scoreOverridden={scoreOverridden}
                onResetScore={handleResetScore}
              />
            </div>
            {contactInScoreRow && (
              <div className="shrink-0">
                <ExpertContactPopover pro={pro} project={project} />
              </div>
            )}
          </div>
        )}

        {(recommendationCheck || isRankingMode) && (
          <div className="flex items-center justify-between gap-2 min-w-0 my-2">
            <div className="min-w-0 flex-1">
              <ScoreRatePills
                expert={pro}
                showRequired={!isRankingMode}
                hideRate={forceHideRate}
                forceHideScore={!!publicToken}
              />
            </div>
            {contactInScoreRow && (
              <div className="shrink-0">
                <ExpertContactPopover pro={pro} project={project} />
              </div>
            )}
          </div>
        )}

        {/* Unselected pros (the search pool) have no stored project score yet.
            Show the algorithm best-fit as a read-only default so internal users
            can gauge fit before adding them. */}
        {!isSelected &&
          !recommendationCheck &&
          !isRankingMode &&
          isInternal(role) &&
          algoMax !== undefined && (
            <ScoreRatePills
              className="my-2"
              displayScore={algoMax}
              hideRate
            />
          )}

        {/* Only render when the caller actually has products in scope —
            dashboard/featured pages pass nothing so this stays hidden. */}
        {algoProducts.length > 0 && (
          <div className="flex items-center justify-between gap-2 min-w-0 mb-2">
            <div className="min-w-0 flex-1">
              <AlgoScorePerProduct
                products={algoProducts}
                scores={algoScores || {}}
                breakdowns={algoBreakdown || {}}
                industryScores={algoIndustryScores || {}}
                expertTypes={algoExpertTypes || {}}
                expertName={pro?.displayName || pro?.firstName || ""}
                professions={algoProfessions || []}
              />
            </div>
            {showContact && (
              <div className="shrink-0">
                <ExpertContactPopover pro={pro} project={project} />
              </div>
            )}
          </div>
        )}
      </div>
      
      {!hideKeyPoints && (
          <div className="flex items-start justify-between gap-2 min-h-0 flex-shrink-0 px-4">
            <div className="flex-1 min-h-0">
              <ExpertKeyPointsList
                expert={pro}
                project={project}
                parentRef={infoPanelRef}
                showProjectSpecific={approvalCheck}
                defaultHeight={150}
              />
            </div>
          </div>
        )}

      <div className="mt-auto pt-3 px-4 pb-4">
        <Button
          variant="soft"
          color="default"
          className="w-full"
          onClick={() =>
            navigate(
              publicToken
                ? `/experts/share?proId=${pro.userid}${project?.id ? `&project=${project.id}` : ''}&token=${publicToken}`
                : `/experts/${pro.userid}/${pro.slug}?proId=${pro.userid}${project?.id ? `&project=${project.id}` : ''}`
            )
          }
        >
          View Profile
        </Button>
      </div>

      {showKeyPointsModal && (
        <ExpertKeyPointsModal
          open={showKeyPointsModal}
          onClose={() => setShowKeyPointsModal(false)}
          expert={pro}
          project={project}
          onUpdate={onUpdate}
        />
      )}

      <ConfirmationDialog
        open={showRemoveConfirm}
        onClose={() => setShowRemoveConfirm(false)}
        onConfirm={() => {
          setShowRemoveConfirm(false);
          performCheckboxChange(false);
        }}
        title="Remove Expert"
        description={`Removing ${pro?.displayName || "this expert"} also removes them from ${describeSharedSiblings(sharedSiblingProjects)}, which share this project's expert selection.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default ExpertSelectionCard;
